import type { BrandingUploadStartResponse, MediaOverlayUploadStartResponse, PaymentUploadStartResponse, UploadStartResponse } from "@ebook/contracts";

type ProgressCallback = (percent: number) => void;

async function assetTransferError(response: Response): Promise<Error> {
  const data = await response.json().catch(() => null) as
    | { error?: { message?: string } }
    | null;
  const message = data?.error?.message
    ? `Asset transfer failed (${response.status}): ${data.error.message}`
    : `Asset transfer failed (${response.status})`;
  return new Error(message);
}

async function workerRequest<T>(
  url: string,
  token: string,
  options: RequestInit,
): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: { ...options.headers, authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await assetTransferError(response);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function multipartUpload(
  file: File,
  upload: UploadStartResponse,
  onProgress: ProgressCallback,
  signal?: AbortSignal,
): Promise<{ workerUploadId: string; parts: Array<{ partNumber: number; etag: string }> }> {
  if (upload.uploadUrl) {
    const partSize = Math.max(1, upload.partSize);
    try {
      for (let offset = 0; offset < file.size; offset += partSize) {
        const end = Math.min(offset + partSize, file.size) - 1;
        const blob = file.slice(offset, end + 1);
        const response = await fetch(upload.uploadUrl, {
          method: "PUT",
          body: blob,
          signal,
          headers: {
            "content-type": file.type || "application/pdf",
            "content-range": `bytes ${offset}-${end}/${file.size}`,
          },
        });
        if (!response.ok) throw await assetTransferError(response);
        onProgress(Math.round(((end + 1) / file.size) * 100));
      }
    } catch (error) {
      if (upload.abortUrl) {
        await fetch(upload.abortUrl, { method: "DELETE" }).catch(() => undefined);
      }
      throw error;
    }
    onProgress(100);
    return { workerUploadId: "local", parts: [{ partNumber: 1, etag: "local" }] };
  }
  const created = await workerRequest<{ uploadId: string }>(
    `${upload.assetWorkerUrl}/multipart/create`,
    upload.grants.create,
    { method: "POST", signal },
  );
  const parts: Array<{ partNumber: number; etag: string }> = [];
  try {
    for (let offset = 0, partNumber = 1; offset < file.size; offset += upload.partSize, partNumber += 1) {
      const blob = file.slice(offset, Math.min(offset + upload.partSize, file.size));
      const result = await workerRequest<{ partNumber: number; etag: string }>(
        `${upload.assetWorkerUrl}/multipart/${encodeURIComponent(created.uploadId)}/parts/${partNumber}`,
        upload.grants.part,
        {
          method: "PUT",
          body: blob,
          signal,
          headers: { "content-type": "application/octet-stream" },
        },
      );
      parts.push(result);
      onProgress(Math.round((Math.min(offset + upload.partSize, file.size) / file.size) * 100));
    }
    await workerRequest(
      `${upload.assetWorkerUrl}/multipart/${encodeURIComponent(created.uploadId)}`,
      upload.grants.complete,
      {
        method: "POST",
        signal,
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ parts }),
      },
    );
    return { workerUploadId: created.uploadId, parts };
  } catch (error) {
    await workerRequest(
      `${upload.assetWorkerUrl}/multipart/${encodeURIComponent(created.uploadId)}`,
      upload.grants.abort,
      { method: "DELETE" },
    ).catch(() => undefined);
    throw error;
  }
}

export async function uploadObjectAsset(
  file: File,
  upload: BrandingUploadStartResponse | MediaOverlayUploadStartResponse | PaymentUploadStartResponse,
  signal?: AbortSignal,
): Promise<void> {
  if (upload.uploadUrl) {
    const response = await fetch(upload.uploadUrl, {
      method: "PUT",
      body: file,
      signal,
      headers: { "content-type": file.type },
    });
    if (!response.ok) throw await assetTransferError(response);
    return;
  }
  const key = upload.objectKey.split("/").map(encodeURIComponent).join("/");
  await workerRequest(
    `${upload.assetWorkerUrl}/objects/${key}`,
    upload.grant,
    {
      method: "PUT",
      body: file,
      signal,
      headers: { "content-type": file.type },
    },
  );
}

export const uploadBrandingAsset = uploadObjectAsset;

export async function uploadPaymentSlip(
  file: File,
  upload: PaymentUploadStartResponse,
  signal?: AbortSignal,
): Promise<void> {
  await uploadObjectAsset(file, upload, signal);
}
